import adapter from "webrtc-adapter";
import type { BroadcasterTelemetrySnapshot } from "ui-common/services/broadcaster-diagnostics/types";

export type { BroadcasterTelemetrySnapshot } from "ui-common/services/broadcaster-diagnostics/types";

type TelemetryMode = "local" | "remote" | "disabled";

interface ClientInfo {
  operatingSystem: string;
  browser: string;
  browserVersion: string;
  deviceType: "desktop" | "mobile" | "tablet" | "unknown";
  deviceName: string;
}

function detectClientInfo(): ClientInfo {
  const ua = navigator.userAgent;
  const platform = navigator.platform || "";

  let operatingSystem = "Unknown";
  if (ua.includes("Win")) operatingSystem = "Windows";
  else if (ua.includes("Mac")) operatingSystem = "macOS";
  else if (ua.includes("Linux")) operatingSystem = "Linux";
  else if (ua.includes("Android")) operatingSystem = "Android";
  else if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) operatingSystem = "iOS";

  const browser = adapter.browserDetails.browser;
  const browserVersion = adapter.browserDetails.version?.toString() ?? "unknown";

  let deviceType: ClientInfo["deviceType"] = "unknown";
  let deviceName = "Unknown";

  if (/Mobile|Android|iPhone/i.test(ua)) {
    deviceType = "mobile";
    if (ua.includes("iPhone")) {
      const match = ua.match(/iPhone\s*(\w+)?/);
      deviceName = match ? `iPhone ${match[1] || ""}`.trim() : "iPhone";
    } else if (ua.includes("Android")) {
      const match = ua.match(/Android.*;\s*([^)]+)\)/);
      deviceName = match?.[1] || "Android Device";
    }
  } else if (/iPad|Tablet/i.test(ua)) {
    deviceType = "tablet";
    deviceName = ua.includes("iPad") ? "iPad" : "Tablet";
  } else {
    deviceType = "desktop";
    if (platform.includes("Mac")) deviceName = "Mac";
    else if (platform.includes("Win")) deviceName = "Windows PC";
    else if (platform.includes("Linux")) deviceName = "Linux PC";
    else deviceName = "Desktop";
  }

  return { operatingSystem, browser, browserVersion, deviceType, deviceName };
}

interface PrevStats {
  videoBytesSent: number;
  videoTimestamp: number;
  audioBytesSent: number;
  audioTimestamp: number;
  framesEncoded: number;
  framesSent: number;
  nackCount: number;
  retransmittedPacketsSent: number;
  packetsSent: number;
  audioPacketsSent: number;
  remotePacketsLost: number;
  qualityLimitationDurations: Record<string, number>;
  qualityLimitationResolutionChanges: number;
  totalEncodeTime: number;
  totalPacketSendDelay: number;
}

export class BroadcasterTelemetry {
  private streamId: string;
  private sessionId: string;
  private pc: RTCPeerConnection;
  private mode: TelemetryMode;
  private clientInfo: ClientInfo;
  private isRunning = false;
  private statsInterval: number | null = null;
  private uploadInterval: number | null = null;
  private snapshots: BroadcasterTelemetrySnapshot[] = [];
  private isUploading = false;
  private prev: PrevStats | null = null;
  private onSnapshot: ((snapshot: BroadcasterTelemetrySnapshot) => void) | null = null;

  private readonly collectionIntervalMs = 1000;
  private readonly uploadIntervalMs = 5000;

  constructor(
    streamId: string,
    sessionId: string,
    pc: RTCPeerConnection,
    mode: TelemetryMode,
    onSnapshot?: (snapshot: BroadcasterTelemetrySnapshot) => void,
  ) {
    this.streamId = streamId;
    this.sessionId = sessionId;
    this.pc = pc;
    this.mode = mode;
    this.clientInfo = detectClientInfo();
    this.onSnapshot = onSnapshot ?? null;
  }

  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;

    void this.collectStats();
    this.statsInterval = window.setInterval(() => {
      void this.collectStats();
    }, this.collectionIntervalMs);

    if (this.mode === "remote") {
      this.uploadInterval = window.setInterval(() => {
        void this.uploadTelemetryBatch();
      }, this.uploadIntervalMs);
    }
  }

  stop(): void {
    if (!this.isRunning) return;
    this.isRunning = false;

    if (this.statsInterval !== null) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }

    if (this.uploadInterval !== null) {
      clearInterval(this.uploadInterval);
      this.uploadInterval = null;
    }

    if (this.mode === "remote") {
      void this.uploadTelemetryBatch();
    }
  }

  private async collectStats(): Promise<void> {
    if (!this.pc || !this.isRunning) return;

    try {
      const stats = await this.pc.getStats();

      let videoBytesSent = 0;
      let videoTimestamp = 0;
      let framesEncoded = 0;
      let framesSent = 0;
      let framesPerSecond = 0;
      let frameWidth = 0;
      let frameHeight = 0;
      let nackCount = 0;
      let retransmittedPacketsSent = 0;
      let packetsSent = 0;
      let qualityLimitationReason: string | undefined;
      let qualityLimitationDurations: Record<string, number> | undefined;
      let qualityLimitationResolutionChanges: number | undefined;
      let targetBitrate: number | undefined;
      let encoderImplementation: string | undefined;
      let totalEncodeTime: number | undefined;
      let totalPacketSendDelay: number | undefined;

      let audioBytesSent = 0;
      let audioTimestamp = 0;
      let audioPacketsSent = 0;

      let currentRtt = 0;
      let availableOutgoingBitrate: number | undefined;

      let remotePacketsLost = 0;
      let remoteJitter = 0;
      let remoteFractionLost = 0;

      stats.forEach((stat) => {
        // Outbound video stats
        if (stat.type === "outbound-rtp" && stat.kind === "video") {
          videoBytesSent = stat.bytesSent || 0;
          videoTimestamp = stat.timestamp || 0;
          framesEncoded = stat.framesEncoded || 0;
          framesSent = stat.framesSent || 0;
          framesPerSecond = stat.framesPerSecond || 0;
          frameWidth = stat.frameWidth || 0;
          frameHeight = stat.frameHeight || 0;
          nackCount = stat.nackCount || 0;
          retransmittedPacketsSent = stat.retransmittedPacketsSent || 0;
          packetsSent = stat.packetsSent || 0;

          // Chrome-only fields
          if (stat.qualityLimitationReason !== undefined) {
            qualityLimitationReason = stat.qualityLimitationReason;
            qualityLimitationDurations = stat.qualityLimitationDurations
              ? { ...stat.qualityLimitationDurations }
              : undefined;
            qualityLimitationResolutionChanges = stat.qualityLimitationResolutionChanges;
            targetBitrate = stat.targetBitrate;
            encoderImplementation = stat.encoderImplementation;
            totalEncodeTime = stat.totalEncodeTime;
            totalPacketSendDelay = stat.totalPacketSendDelay;
          }
        }

        // Outbound audio stats
        if (stat.type === "outbound-rtp" && stat.kind === "audio") {
          audioBytesSent = stat.bytesSent || 0;
          audioTimestamp = stat.timestamp || 0;
          audioPacketsSent = stat.packetsSent || 0;
        }

        // Connection quality — prefer nominated, fallback to succeeded
        if (stat.type === "candidate-pair" && (stat.nominated === true || stat.state === "succeeded")) {
          currentRtt = (stat.currentRoundTripTime || 0) * 1000;
          availableOutgoingBitrate = stat.availableOutgoingBitrate;
        }

        // Remote feedback (from viewer's RTCP reports)
        if (stat.type === "remote-inbound-rtp" && stat.kind === "video") {
          remotePacketsLost = stat.packetsLost || 0;
          remoteJitter = (stat.jitter || 0) * 1000;
          remoteFractionLost = stat.fractionLost || 0;
        }
      });

      // Delta calculations
      const prev = this.prev;

      let videoBitrateSent = 0;
      let deltaFramesEncoded = 0;
      let deltaFramesSent = 0;
      let deltaNackCount = 0;
      let deltaRetransmittedPacketsSent = 0;
      let deltaPacketsSent = 0;
      let audioBitrateSent = 0;
      let deltaAudioPacketsSent = 0;
      let deltaRemotePacketsLost = 0;
      let deltaQualityLimitationDurations: Record<string, number> | undefined;
      let deltaQualityLimitationResolutionChanges: number | undefined;
      let deltaTotalEncodeTime: number | undefined;
      let deltaTotalPacketSendDelay: number | undefined;

      if (prev) {
        // Video bitrate
        const deltaTime = (videoTimestamp - prev.videoTimestamp) / 1000;
        const deltaBytes = videoBytesSent - prev.videoBytesSent;
        videoBitrateSent = deltaTime > 0 ? (deltaBytes * 8) / deltaTime / 1000 : 0;

        // Audio bitrate
        const audioDeltaTime = (audioTimestamp - prev.audioTimestamp) / 1000;
        const audioDeltaBytes = audioBytesSent - prev.audioBytesSent;
        audioBitrateSent = audioDeltaTime > 0 ? (audioDeltaBytes * 8) / audioDeltaTime / 1000 : 0;

        // Counter deltas
        deltaFramesEncoded = framesEncoded - prev.framesEncoded;
        deltaFramesSent = framesSent - prev.framesSent;
        deltaNackCount = nackCount - prev.nackCount;
        deltaRetransmittedPacketsSent = retransmittedPacketsSent - prev.retransmittedPacketsSent;
        deltaPacketsSent = packetsSent - prev.packetsSent;
        deltaAudioPacketsSent = audioPacketsSent - prev.audioPacketsSent;
        deltaRemotePacketsLost = remotePacketsLost - prev.remotePacketsLost;

        // Chrome-only deltas
        if (qualityLimitationDurations && prev.qualityLimitationDurations) {
          deltaQualityLimitationDurations = {};
          for (const key of Object.keys(qualityLimitationDurations)) {
            deltaQualityLimitationDurations[key] =
              (qualityLimitationDurations[key] || 0) - (prev.qualityLimitationDurations[key] || 0);
          }
        }
        if (qualityLimitationResolutionChanges !== undefined) {
          deltaQualityLimitationResolutionChanges =
            qualityLimitationResolutionChanges - prev.qualityLimitationResolutionChanges;
        }
        if (totalEncodeTime !== undefined) {
          deltaTotalEncodeTime = totalEncodeTime - prev.totalEncodeTime;
        }
        if (totalPacketSendDelay !== undefined) {
          deltaTotalPacketSendDelay = totalPacketSendDelay - prev.totalPacketSendDelay;
        }
      }

      // Store current values for next delta
      this.prev = {
        videoBytesSent,
        videoTimestamp,
        audioBytesSent,
        audioTimestamp,
        framesEncoded,
        framesSent,
        nackCount,
        retransmittedPacketsSent,
        packetsSent,
        audioPacketsSent,
        remotePacketsLost,
        qualityLimitationDurations: qualityLimitationDurations ? { ...qualityLimitationDurations } : {},
        qualityLimitationResolutionChanges: qualityLimitationResolutionChanges ?? 0,
        totalEncodeTime: totalEncodeTime ?? 0,
        totalPacketSendDelay: totalPacketSendDelay ?? 0,
      };

      const qualityScore = this.calculateQualityScore({
        qualityLimitationReason,
        currentRtt,
        videoBitrateSent,
        framesPerSecond,
        remoteFractionLost,
        deltaRemotePacketsLost,
        deltaPacketsSent,
      });

      const snapshot: BroadcasterTelemetrySnapshot = {
        timestamp: Date.now(),
        streamId: this.streamId,
        sessionId: this.sessionId,

        videoBitrateSent,
        framesEncoded: deltaFramesEncoded,
        framesSent: deltaFramesSent,
        framesPerSecond,
        frameWidth,
        frameHeight,
        nackCount: deltaNackCount,
        retransmittedPacketsSent: deltaRetransmittedPacketsSent,
        packetsSent: deltaPacketsSent,

        audioBitrateSent,
        audioPacketsSent: deltaAudioPacketsSent,

        currentRtt,
        availableOutgoingBitrate,

        remotePacketsLost: deltaRemotePacketsLost,
        remoteJitter,
        remoteFractionLost,

        qualityLimitationReason,
        qualityLimitationDurations: deltaQualityLimitationDurations,
        qualityLimitationResolutionChanges: deltaQualityLimitationResolutionChanges,
        targetBitrate,
        encoderImplementation,
        totalEncodeTime: deltaTotalEncodeTime,
        totalPacketSendDelay: deltaTotalPacketSendDelay,

        iceConnectionState: this.pc.iceConnectionState,
        videoCodec: this.formatCodecs("video"),
        audioCodec: this.formatCodecs("audio"),
        qualityScore,

        operatingSystem: this.clientInfo.operatingSystem,
        browser: this.clientInfo.browser,
        browserVersion: this.clientInfo.browserVersion,
        deviceType: this.clientInfo.deviceType,
        deviceName: this.clientInfo.deviceName,
      };

      if (this.mode === "remote") {
        this.snapshots.push(snapshot);
      }

      this.onSnapshot?.(snapshot);
    } catch {
      // Stats collection can fail transiently during connection teardown
    }
  }

  private calculateQualityScore(params: {
    qualityLimitationReason?: string;
    currentRtt: number;
    videoBitrateSent: number;
    framesPerSecond: number;
    remoteFractionLost: number;
    deltaRemotePacketsLost: number;
    deltaPacketsSent: number;
  }): number {
    let score = 100;
    const hasQualityLimitation = params.qualityLimitationReason !== undefined;

    if (hasQualityLimitation) {
      // Chrome/Edge path
      // Quality limitation penalty (max 40)
      switch (params.qualityLimitationReason) {
        case "bandwidth":
          score -= 40;
          break;
        case "cpu":
          score -= 30;
          break;
        case "other":
          score -= 10;
          break;
      }

      // RTT penalty (max 25)
      if (params.currentRtt > 300) score -= 25;
      else if (params.currentRtt > 200) score -= 20;
      else if (params.currentRtt > 150) score -= 15;
      else if (params.currentRtt > 100) score -= 10;
      else if (params.currentRtt > 50) score -= 5;

      // Bitrate vs target (4500kbps) penalty (max 20)
      const bitrateRatio = params.videoBitrateSent / 4500;
      if (bitrateRatio < 0.3) score -= 20;
      else if (bitrateRatio < 0.5) score -= 15;
      else if (bitrateRatio < 0.7) score -= 10;
      else if (bitrateRatio < 0.85) score -= 5;

      // FPS penalty (max 15)
      if (params.framesPerSecond < 10) score -= 15;
      else if (params.framesPerSecond < 15) score -= 10;
      else if (params.framesPerSecond < 20) score -= 5;

      // Remote packet loss penalty (max 15)
      const lossRate =
        params.deltaPacketsSent > 0 ? (params.deltaRemotePacketsLost / params.deltaPacketsSent) * 100 : 0;
      if (lossRate > 5) score -= 15;
      else if (lossRate > 2) score -= 10;
      else if (lossRate > 1) score -= 5;
    } else {
      // Firefox/Safari fallback

      // Bitrate penalty (max 35)
      const bitrateRatio = params.videoBitrateSent / 4500;
      if (bitrateRatio < 0.3) score -= 35;
      else if (bitrateRatio < 0.5) score -= 25;
      else if (bitrateRatio < 0.7) score -= 15;
      else if (bitrateRatio < 0.85) score -= 5;

      // FPS penalty (max 25)
      if (params.framesPerSecond < 10) score -= 25;
      else if (params.framesPerSecond < 15) score -= 20;
      else if (params.framesPerSecond < 20) score -= 10;

      // Remote packet loss penalty (max 25)
      const lossRate =
        params.deltaPacketsSent > 0 ? (params.deltaRemotePacketsLost / params.deltaPacketsSent) * 100 : 0;
      if (lossRate > 5) score -= 25;
      else if (lossRate > 2) score -= 15;
      else if (lossRate > 1) score -= 5;

      // RTT penalty (max 25)
      if (params.currentRtt > 300) score -= 25;
      else if (params.currentRtt > 200) score -= 20;
      else if (params.currentRtt > 150) score -= 15;
      else if (params.currentRtt > 100) score -= 10;
      else if (params.currentRtt > 50) score -= 5;
    }

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  private formatCodecs(kind: "audio" | "video"): string {
    if (!this.pc) return "";

    const codecs: string[] = [];
    this.pc.getSenders().forEach((sender) => {
      if (!sender.track || sender.track.kind !== kind) return;

      const params = sender.getParameters();
      params?.codecs?.forEach((codec) => {
        if (codec.mimeType.includes("/red") || codec.mimeType.includes("/rtx") || codec.mimeType.includes("/fec")) {
          return;
        }

        const parts: string[] = [];
        parts.push(codec.mimeType.replace("audio/", "").replace("video/", ""));
        parts.push(`${codec.clockRate}Hz`);

        if (codec.channels && sender.track && sender.track.kind === "audio") {
          parts.push(`${codec.channels}ch`);
        }

        parts.push(`pt:${codec.payloadType}`);
        codecs.push(parts.join(" "));
      });
    });

    return codecs.join(", ");
  }

  private uploadTelemetryBatch(): void {
    if (this.isUploading) return;
    if (this.snapshots.length === 0) return;

    this.isUploading = true;
    const batch = this.snapshots;
    this.snapshots = [];

    fetch("/api/rpc/ingest-broadcaster-telemetry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        snapshots: batch.map((s) => ({
          session_id: s.sessionId,
          stream_id: s.streamId,
          timestamp: s.timestamp,
          video_bitrate_sent: s.videoBitrateSent,
          frames_encoded: s.framesEncoded,
          frames_sent: s.framesSent,
          frames_per_second: s.framesPerSecond,
          frame_width: s.frameWidth,
          frame_height: s.frameHeight,
          packets_sent: s.packetsSent,
          retransmitted_packets_sent: s.retransmittedPacketsSent,
          nack_count: s.nackCount,
          current_rtt: s.currentRtt,
          available_outgoing_bitrate: s.availableOutgoingBitrate,
          audio_bitrate_sent: s.audioBitrateSent,
          audio_packets_sent: s.audioPacketsSent,
          remote_packets_lost: s.remotePacketsLost,
          remote_jitter: s.remoteJitter,
          remote_fraction_lost: s.remoteFractionLost,
          quality_limitation_reason: s.qualityLimitationReason,
          target_bitrate: s.targetBitrate,
          encoder_implementation: s.encoderImplementation,
          ice_connection_state: s.iceConnectionState,
          video_codec: s.videoCodec,
          audio_codec: s.audioCodec,
          quality_score: s.qualityScore,
          operating_system: s.operatingSystem,
          browser: s.browser,
          browser_version: s.browserVersion,
          device_type: s.deviceType,
          device_name: s.deviceName,
        })),
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(() => {})
      .catch(() => {
        // Keep data for retry by prepending failed batch
        this.snapshots = [...batch, ...this.snapshots];
      })
      .finally(() => {
        this.isUploading = false;
      });
  }
}
